import { HidePassword, ShowPassword } from 'assets/Icons';
import { Button, Container, Spacer } from 'components/atoms';
import { InputField } from 'components/atoms/InputField/InputField';
import { LOGIN } from 'navigation/routes';
import { useMutation } from 'network/mutations/useMutationHook';
import { login, register } from 'network/services/auth';
import { enqueueSnackbar } from 'notistack';
import { ChangeEvent, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import type { CustomerRegisterResponse } from 'TYPES/api.body';
import { isOtpRequired, persistCustomerAuth, resolvePostAuthPath } from 'utils/customerAuth';
import { userIsLoggedIn, userRoleAtom } from 'store/atoms/userAtom';
import { validators } from 'utils/validator';
import {
  AltSignUp,
  AuthContainer,
  buttonStyle,
  extraStylesInput,
  extraStylesInputPhone,
  InputCon,
  TogglePassword
} from './styles';

const INITIALSTATE = {
  email: '',
  password: '',
  phoneNumber: '',
  lastname: '',
  firstname: ''
};

const SignUp = () => {
  const [searchParam] = useSearchParams();
  const referralCode = searchParam.get('referralCode');
  const [formData, setFormData] = useState(INITIALSTATE);
  const [formError, setFormError] = useState(INITIALSTATE);

  const [agreement, setAgreement] = useState(false);

  const onToggleAgreement = (e: ChangeEvent<HTMLInputElement>) => {
    setAgreement(e.target.checked);
  };

  const onUpdateFormData = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    const error = validators[name](value);
    setFormData(formData => ({ ...formData, [name]: value }));
    setFormError({
      ...formError,
      [name]: error
    });
  };

  const navigate = useNavigate();
  const setUserRole = useSetRecoilState(userRoleAtom);
  const setUserStatus = useSetRecoilState(userIsLoggedIn);

  const [showPassword, setShowPassword] = useState<'text' | 'password'>('password');

  const onTogglePassword = () => {
    if (showPassword === 'text') return setShowPassword('password');
    setShowPassword('text');
  };

  const { mutateAsync, isLoading } = useMutation(register, 'SIGNUP');

  const _register = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    try {
      const completeData =
        referralCode !== null ? { ...formData, referralCode } : formData;
      const { data } = await mutateAsync(completeData);
      const registerResponse = data as CustomerRegisterResponse;

      if (isOtpRequired(registerResponse)) {
        enqueueSnackbar(
          registerResponse.message ?? 'Registration successful. Please check your email for OTP.',
          { variant: 'info' }
        );
        navigate(`/otp/${encodeURIComponent(formData.email)}`);
        return;
      }

      enqueueSnackbar(registerResponse.message ?? 'Registration successful.', { variant: 'success' });

      const email = formData.email.trim().toLowerCase();
      const loginResponse = await login({
        identifier: email,
        email,
        password: formData.password
      });
      const loginData = loginResponse.data;

      if (loginData?.status === 'success' && loginData.access_token) {
        persistCustomerAuth({ access_token: loginData.access_token, user: loginData.user });
        if (loginData.user?.role) {
          setUserRole(loginData.user.role);
        }
        setUserStatus(true);
        navigate(resolvePostAuthPath());
        return;
      }

      enqueueSnackbar('Account created. Please log in.', { variant: 'info' });
      navigate(LOGIN);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'An error occurred', { variant: 'error' });
    }
  };

  const disabled =
    !formData.email ||
    !formData.password ||
    !formData.phoneNumber ||
    !!validators.email(formData.email) ||
    !!validators.password(formData.password);

  return (
    <AuthContainer>
        <div className="">
          <h1 className="text-center">Create an account</h1>
          <p className="option text-center">
            Already have an account? <span onClick={() => navigate(LOGIN)}>Log in</span>
          </p>

          <Spacer height="1rem" />

            <Container>
              <form className="sign-up">
                <AltSignUp>
                  <InputCon>
                    <span>Firstname</span>
                    <InputField
                      name="firstname"
                      onChange={onUpdateFormData}
                      placeholder="Enter a name"
                      extraStyles={extraStylesInput}
                      required={true}
                      error={formError.firstname}
                    />
                  </InputCon>
                  <Spacer height="2rem" width="2rem" />
                  <InputCon>
                    <span>Lastname</span>
                    <InputField
                      name="lastname"
                      onChange={onUpdateFormData}
                      placeholder="Enter a name"
                      extraStyles={extraStylesInput}
                      required={true}
                      error={formError.lastname}
                    />
                  </InputCon>
                </AltSignUp>
                <Spacer height="1.5rem" />
                <InputCon>
                  <span>Email Address</span>
                  <InputField
                    type="email"
                    name="email"
                    onChange={onUpdateFormData}
                    placeholder="example@mail.com"
                    extraStyles={extraStylesInput}
                    required={true}
                    error={formError.email}
                    autoComplete="username"
                  />
                </InputCon>
                <Spacer height="1.5rem" />
                <InputCon>
                  <span>Phone Number</span>
                  <PhoneInput
                    country={'ng'}
                    value={formData.phoneNumber}
                    onChange={phone => {
                      setFormData({ ...formData, phoneNumber: phone });
                    }}
                    placeholder={'80XXXXXXXX'}
                    buttonStyle={buttonStyle}
                    autoFormat={false}
                    inputStyle={extraStylesInputPhone}
                  // error={formError.phoneNumber}
                  />
                </InputCon>
                <Spacer height="1.5rem" />
                <InputCon>
                  <span>Password</span>
                  <InputField
                    type={showPassword}
                    name="password"
                    onChange={onUpdateFormData}
                    placeholder="enter your password"
                    extraStyles={extraStylesInput}
                    required={true}
                    error={formError.password}
                    action={
                      <TogglePassword onClick={onTogglePassword}>
                        {showPassword !== 'text' ? <ShowPassword /> : <HidePassword />}
                      </TogglePassword>
                    }
                    autoComplete="new-password"
                  />
                </InputCon>

                <div className="policy">
                  <input type="checkbox" name="policy" id="policy" onChange={onToggleAgreement} />
                  <p className="option">
                    I have read, understood and I agree to Madina’s
                    <Link className="text-primary font-bold" to="/privacy-policy">
                      {' '}
                      Privacy Policy
                    </Link>
                    , and{' '}
                    <Link className="text-primary font-bold" to="/terms-and-conditions">
                      Terms and conditions
                    </Link>
                    .
                  </p>
                </div>

                <div className="buttonContainer">
                  <Button
                    isLoading={isLoading}
                    type="submit"
                    onClick={_register}
                    disabled={disabled || isLoading || !agreement}>
                    Create Account
                  </Button>
                </div>
              </form>
            </Container>
        </div>
    </AuthContainer>
  );
};

export default SignUp;
